import { ChevronDownIcon } from "Icons/ChevronDown";

import { useRef, useState } from "react";

import { SerializedStyles, css } from "@emotion/react";
import { EmotionJSX } from "@emotion/react/types/jsx-namespace";

import { useCloseOnClickOutside } from "utils/libraryHooks";

import { uiGray } from "constants/colors";

import { Input } from "./Input";
import { OptionType, OptionsMenu } from "./OptionsMenu";

type Props<T> = {
  label: string;
  onSelect: (value: string) => void;
  onChange?: (value: string) => string | undefined;
  placeholder?: string;
  options: T[];
  OptionComponent?: (props: T) => EmotionJSX.Element;
  OptionFooter?: (props: {
    searchText: string;
    onClick: () => void;
  }) => EmotionJSX.Element;
  OptionHeader?: (props: {
    searchText: string;
    onClick: () => void;
  }) => EmotionJSX.Element;
  enableHeaderSelectOnClickEnter?: boolean; // when truthy, the select on click enter behaviour will be extended to the header
  enableFooterSelectOnClickEnter?: boolean; // when truthy, the select on click enter behaviour will be extended to the footer
  PreTabComponents?: EmotionJSX.Element[];
  selectedValue?: string;
  resetSearchTextOnSelect?: boolean;
  setSearchTextOnSelect?: boolean;
  filterFn?: (searchText: string) => (option: T) => boolean;
  onBackspaceRightAfterPreTab?: () => void;
  customCss?: {
    optionsMenu?: SerializedStyles;
    input?: SerializedStyles;
    wrapper?: SerializedStyles;
  };
  closeOnSelect?: boolean;
  isOptional?: boolean;
  hideOptionsMenu?: boolean;
  customEmptyStateLabel?: string;
  displayLabel?: boolean;
  SuffixComponent?: EmotionJSX.Element;
};

export const InputSelect = <T extends OptionType>({
  label,
  placeholder,
  options,
  OptionComponent,
  OptionFooter,
  OptionHeader,
  enableHeaderSelectOnClickEnter = false,
  enableFooterSelectOnClickEnter = false,
  PreTabComponents,
  onSelect,
  onChange,
  onBackspaceRightAfterPreTab,
  selectedValue,
  resetSearchTextOnSelect = false,
  setSearchTextOnSelect,
  filterFn = (searchText: string) => (option: T) =>
    option.label.toLowerCase().includes(searchText.toLowerCase()),
  customCss,
  closeOnSelect = false,
  isOptional,
  hideOptionsMenu = false,
  displayLabel = true,
  SuffixComponent = (
    <ChevronDownIcon width={16} height={16} stroke={uiGray[90]} />
  ),
}: Props<T>) => {
  const [showOptions, setShowOptions] = useState(false);
  const [searchText, setSearchText] = useState(selectedValue || "");
  const inputRef = useRef<HTMLInputElement>(null);

  const listId = `input-select-${label.replaceAll(" ", "-")}`; // this is used to differentiate when multiple dropdown on same page
  useCloseOnClickOutside(showOptions, setShowOptions, listId);
  const optionsListId = `options-list-${listId}`;
  const headerId = `options-header-${listId}`;
  const footerId = `options-footer-${listId}`;

  return (
    <div css={[css({ position: "relative" }), customCss?.wrapper]} id={listId}>
      <Input
        isOptional={isOptional}
        customCss={{ wrapper: customCss?.input }}
        inputRef={inputRef}
        PreTabComponents={PreTabComponents}
        onChange={(value: string) => {
          if (onChange) {
            const remainder = onChange(value);
            setSearchText(remainder || "");
          } else {
            setSearchText(value);
          }
        }}
        value={searchText}
        label={label}
        displayLabel={displayLabel}
        placeholder={placeholder}
        SuffixComponent={!showOptions ? SuffixComponent : undefined}
        onFocus={() => setShowOptions(true)}
        onEnter={() => {
          // get all the buttons from the list
          const optionsList = document.getElementById(optionsListId);
          let buttons = Array.from(
            optionsList?.getElementsByTagName("button") || []
          );
          // get the buttons from header component, if enabled for select on click enter
          if (enableHeaderSelectOnClickEnter) {
            const header = document.getElementById(headerId);
            const headerButtons = Array.from(
              header?.getElementsByTagName("button") || []
            );
            buttons = buttons.concat(headerButtons);
          }
          // get the buttons from footer component, if enabled for select on click enter
          if (enableFooterSelectOnClickEnter) {
            const footer = document.getElementById(footerId);
            const footerButtons = Array.from(
              footer?.getElementsByTagName("button") || []
            );

            buttons = buttons.concat(footerButtons);
          }
          // get all the active options (i.e. not disabled)
          const activeButtons = buttons.filter((button) => !button.disabled);
          // if there is only one, we should select it automatically on enter
          if (activeButtons.length === 1) {
            activeButtons[0].click();
          }
        }}
        onBackspace={() => {
          // if there is no searchtext the cursor is right after the PreTabComponents
          // when pressing backspace we should trigger onBackspaceRightAfterPreTab
          if (!searchText.length && onBackspaceRightAfterPreTab)
            onBackspaceRightAfterPreTab();
        }}
      />
      {!hideOptionsMenu && (
        <OptionsMenu
          customEmptyStateLabel={
            !searchText ? "Start typing to see options" : undefined
          }
          customCss={{ wrapper: customCss?.optionsMenu }}
          optionsListId={optionsListId}
          options={options.filter(filterFn(searchText))}
          onChange={(option) => {
            onSelect(option.name);
            if (resetSearchTextOnSelect) setSearchText("");
            if (setSearchTextOnSelect) setSearchText(option.label);
            if (closeOnSelect) {
              setShowOptions(false);
            } else {
              inputRef.current?.focus();
            }
          }}
          isVisible={showOptions}
          OptionComponent={OptionComponent}
          FooterComponent={
            OptionFooter ? (
              <div id={footerId}>
                <OptionFooter
                  searchText={searchText}
                  onClick={() => {
                    if (resetSearchTextOnSelect) setSearchText("");
                    if (closeOnSelect) {
                      setShowOptions(false);
                      inputRef.current?.blur();
                    }
                  }}
                />
              </div>
            ) : undefined
          }
          HeaderComponent={
            OptionHeader ? (
              <div id={headerId}>
                <OptionHeader
                  searchText={searchText}
                  onClick={() => {
                    if (resetSearchTextOnSelect) setSearchText("");
                    if (closeOnSelect) setShowOptions(false);
                  }}
                />
              </div>
            ) : undefined
          }
        />
      )}
    </div>
  );
};
